(() => {
  if (customElements.get('media-mall-x-feed')) return;

  function httpsURL(value, hosts) {
    try {
      const url = new URL(value);
      return url.protocol === 'https:' && (!hosts || hosts.includes(url.hostname)) ? url.href : '';
    } catch { return ''; }
  }

  function element(tag, className, text) {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text) node.textContent = text;
    return node;
  }

  function appendText(node, text) {
    // Treat post text as text, never HTML. Only HTTPS links become clickable.
    const parts = text.split(/(https:\/\/[^\s<>]+)/g);
    for (const part of parts) {
      const href = httpsURL(part);
      if (!href) { node.append(document.createTextNode(part)); continue; }
      const link = element('a', '', part);
      link.href = href;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      node.append(link);
    }
  }

  function decodeText(text) {
    const named = { amp: '&', lt: '<', gt: '>', quot: '"', apos: "'", nbsp: ' ' };
    return text.replace(/&(#x[0-9a-f]+|#\d+|amp|lt|gt|quot|apos|nbsp);/gi, (match, entity) => {
      if (entity[0] !== '#') return named[entity.toLowerCase()] || match;
      const value = entity[1].toLowerCase() === 'x' ? parseInt(entity.slice(2), 16) : parseInt(entity.slice(1), 10);
      return value > 0 && value <= 0x10ffff && !(value >= 0xd800 && value <= 0xdfff) ? String.fromCodePoint(value) : match;
    });
  }

  function card(post, profile) {
    const item = element('li');
    const article = element('article', 'x-feed-card');
    const content = element('div', 'x-feed-content');
    const meta = element('div', 'x-feed-meta');
    const avatar = element('span', 'x-feed-avatar', 'MM');
    avatar.setAttribute('aria-hidden', 'true');
    const avatarURL = httpsURL(profile?.image, ['pbs.twimg.com']);
    if (avatarURL) {
      const image = element('img');
      image.src = avatarURL;
      image.alt = '';
      image.width = 48;
      image.height = 48;
      image.loading = 'lazy';
      image.addEventListener('error', () => avatar.replaceChildren(document.createTextNode('MM')), { once: true });
      avatar.replaceChildren(image);
    }
    const mark = element('span', 'x-feed-mark', '𝕏');
    mark.setAttribute('aria-hidden', 'true');
    const author = element('div', 'x-feed-author');
    author.append(element('strong', '', 'Media Mall'), element('span', '', '@mediamallstore'));
    meta.append(avatar, author, mark);
    const text = element('p', 'x-feed-text');
    appendText(text, decodeText(post.text));
    content.append(text);
    const footer = element('footer', 'x-feed-footer');

    const date = new Date(post.createdAt);
    if (Number.isFinite(date.getTime())) {
      const time = element('time', '', new Intl.DateTimeFormat(document.documentElement.lang || 'en', {
        month: 'short', day: 'numeric', year: 'numeric',
      }).format(date));
      time.dateTime = date.toISOString();
      time.className = 'x-feed-date';
      footer.append(time);
    }
    const postLink = element('a', 'x-feed-link', 'View post on X');
    postLink.href = `https://x.com/mediamallstore/status/${post.id}`;
    postLink.target = '_blank';
    postLink.rel = 'noopener noreferrer';
    postLink.setAttribute('aria-label', `View Media Mall post on X: ${decodeText(post.text).slice(0, 70)} (opens in a new tab)`);
    const arrow = element('span', '', '↗');
    arrow.setAttribute('aria-hidden', 'true');
    postLink.append(arrow);
    footer.append(postLink);
    article.append(meta, content);

    const media = element('div', 'x-feed-media');
    for (const attachment of (Array.isArray(post.media) ? post.media : []).slice(0, 4)) {
      const imageURL = httpsURL(attachment.image, ['pbs.twimg.com']);
      const videoURL = httpsURL(attachment.video, ['video.twimg.com']);
      if (videoURL) {
        const video = element('video');
        video.src = videoURL;
        if (imageURL) video.poster = imageURL;
        video.controls = true;
        video.playsInline = true;
        video.preload = 'none';
        video.setAttribute('aria-label', attachment.alt || 'Video from Media Mall');
        media.append(video);
      } else if (imageURL) {
        const image = element('img');
        image.src = imageURL;
        image.alt = attachment.alt || 'Image from Media Mall’s post';
        image.loading = 'lazy';
        image.decoding = 'async';
        media.append(image);
      }
    }
    if (media.childElementCount) article.append(media);
    article.append(footer);
    item.append(article);
    return item;
  }

  class MediaMallXFeed extends HTMLElement {
    connectedCallback() { this.load(); }
    disconnectedCallback() { this.controller?.abort(); }

    async load() {
      this.controller?.abort();
      const controller = new AbortController();
      this.controller = controller;
      const timeout = setTimeout(() => controller.abort(), 10000);
      try {
        const endpoint = httpsURL(this.dataset.endpoint);
        if (!endpoint) throw new Error('Configure the X feed endpoint in this section’s settings.');
        const response = await fetch(endpoint, { signal: controller.signal, credentials: 'omit' });
        if (!response.ok) throw new Error('The X feed is not ready. Check the Worker token, cache, and scheduled refresh.');
        const feed = await response.json();
        if (feed.account !== 'mediamallstore' || !Array.isArray(feed.posts)) throw new Error('Invalid X feed response.');
        const posts = feed.posts.filter(post => /^\d+$/.test(post.id) && typeof post.text === 'string').slice(0, 3);
        if (!posts.length) throw new Error('No public posts are available in the X feed yet.');
        const list = element('ul', 'x-feed-grid');
        list.setAttribute('aria-label', 'Recent posts from Media Mall');
        posts.forEach(post => list.append(card(post, feed.author)));
        this.replaceChildren(list);
        this.hidden = false;
      } catch (error) {
        if (this.controller !== controller || !this.isConnected) return;
        this.hidden = true;
        if (this.dataset.designMode === 'true') {
          this.replaceChildren(element('p', 'x-feed-status', error.name === 'AbortError' ? 'X feed request timed out.' : error.message));
          this.hidden = false;
        }
      } finally { clearTimeout(timeout); }
    }
  }
  customElements.define('media-mall-x-feed', MediaMallXFeed);
})();
